/**
 * Undo/redo history of definition snapshots (immer structural sharing keeps these cheap).
 * Typing on the same field within COALESCE_MS updates the current snapshot instead of pushing a new one.
 */
export const HISTORY_LIMIT = 100;
export const COALESCE_MS = 1000;

export interface History<T> {
  present: T;
  past: T[];
  future: T[];
  lastKey?: string;
  lastAt?: number;
}

export function createHistory<T>(present: T): History<T> {
  return { present, past: [], future: [] };
}

export function commit<T>(h: History<T>, next: T, key?: string, now: number = Date.now()): History<T> {
  if (Object.is(next, h.present)) return h;
  const coalesce = key !== undefined && key === h.lastKey && h.lastAt !== undefined && now - h.lastAt < COALESCE_MS;
  const past = coalesce ? h.past : [...h.past, h.present].slice(-HISTORY_LIMIT);
  return { present: next, past, future: [], lastKey: key, lastAt: now };
}

export function undo<T>(h: History<T>): History<T> {
  const prev = h.past[h.past.length - 1];
  if (prev === undefined) return h;
  return { present: prev, past: h.past.slice(0, -1), future: [h.present, ...h.future], lastKey: undefined, lastAt: undefined };
}

export function redo<T>(h: History<T>): History<T> {
  const next = h.future[0];
  if (next === undefined) return h;
  return { present: next, past: [...h.past, h.present].slice(-HISTORY_LIMIT), future: h.future.slice(1), lastKey: undefined, lastAt: undefined };
}

/** Replace the present without an undo step (e.g. applying the server's version after a reload). */
export function reset<T>(present: T): History<T> {
  return createHistory(present);
}
